import React, { useState, useEffect } from "react";
import { NavLink, Navigate, Routes, Route, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Wallet, TrendingUp, CreditCard, ListOrdered, Heart,
  ArrowLeftRight, Users, Send, UserPlus, Shield, User, Bot, Bell, LogOut,
  Building2, QrCode, BarChart3, Code, Package, UsersRound, Landmark, FileText,
  Menu, X, Globe, ShoppingBag, GraduationCap
} from "lucide-react";
import { useStore } from "../store";
import api from "../api";
import "./dashboard.css";

import {
  OverviewPage, WalletsPage, StakingPage, CardPage, TransactionsPage,
  CharityPage, ExchangePage, P2PPage, SwiftPage, ReferralPage, SecurityPage,
  ProfilePage, MarketplacePage, AcademyPage,
} from "./user";
import {
  BizOverviewPage, AcquiringPage, SwiftBizPage, AnalyticsPage, ApiPage,
  CatalogPage, TeamPage, WithdrawPage, DocsPage,
} from "./business";
import {
  AdminOverviewPage, AdminUsersPage, AdminKycPage, AdminTransactionsPage,
  AdminOrdersPage, AdminProductsPage, AdminLeaderboardPage, AdminInvestorLeadsPage,
} from "./admin";
import { useT } from "../site/i18n";

const buildUserNav = (t) => [
  { group: t("side.group.overview"), items: [["", t("nav.overview"), LayoutDashboard]] },
  { group: t("side.group.finance"),  items: [
    ["wallets",      t("nav.wallets"),      Wallet],
    ["staking",      t("nav.staking"),      TrendingUp],
    ["card",         t("nav.visa"),         CreditCard],
    ["transactions", t("nav.transactions"), ListOrdered],
    ["charity",      t("nav.charity"),      Heart],
  ]},
  { group: t("side.group.trade"), items: [
    ["exchange", t("nav.exchange"), ArrowLeftRight],
    ["p2p",      t("nav.p2p"),      Users],
    ["swift",    t("nav.swift"),    Send],
  ]},
  { group: t("side.group.network"), items: [
    ["referrals",   t("nav.referrals"),   UserPlus],
    ["marketplace", t("nav.marketplace"), ShoppingBag],
    ["academy",     t("nav.academy"),     GraduationCap],
  ]},
  { group: t("side.group.account"), items: [
    ["security", t("nav.security"), Shield],
    ["profile",  t("nav.profile"),  User],
  ]},
];

const buildBizNav = (t) => [
  { group: t("side.group.business"), items: [["biz", t("nav.biz_overview"), LayoutDashboard]] },
  { group: t("side.group.payments"), items: [
    ["biz/acquiring", t("nav.biz_acquiring"), QrCode],
    ["biz/swift",     t("nav.biz_swift"),     Send],
    ["biz/withdraw",  t("nav.biz_withdraw"),  Landmark],
  ]},
  { group: t("side.group.services"), items: [
    ["biz/analytics", t("nav.biz_analytics"), BarChart3],
    ["biz/catalog",   t("nav.biz_catalog"),   Package],
    ["biz/api",       t("nav.biz_api"),       Code],
  ]},
  { group: t("side.group.management"), items: [
    ["biz/team", t("nav.biz_team"), UsersRound],
    ["biz/docs", t("nav.biz_docs"), FileText],
  ]},
];

const buildAdminNav = (t) => [
  { group: t("side.group.admin"), items: [["admin", t("nav.admin_overview"), LayoutDashboard]] },
  { group: t("side.group.management"), items: [
    ["admin/users",       t("nav.admin_users"),    UsersRound],
    ["admin/kyc",         t("nav.admin_kyc"),      Shield],
    ["admin/products",    t("nav.admin_products"), Package],
    ["admin/leaderboard", "🏆 Лидерборд",          BarChart3],
    ["admin/investor-leads", "💼 Заявки инвесторов", Shield],
  ]},
  { group: t("side.group.records"), items: [
    ["admin/transactions", t("nav.admin_transactions"), ListOrdered],
    ["admin/orders",       t("nav.admin_orders"),       ShoppingBag],
  ]},
];

export default function Dashboard() {
  const { token, user, wallets: storeWallets, fetchAll, logout } = useStore();
  const navigate = useNavigate();
  const { t } = useT();
  const USER_NAV = buildUserNav(t);
  const BIZ_NAV = buildBizNav(t);
  const ADMIN_NAV = buildAdminNav(t);
  const [role, setRole] = useState(() => localStorage.getItem("dash_role") || "user");
  const [bellOpen, setBellOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);

  useEffect(() => { if (token) fetchAll().catch(() => {}); }, [token, fetchAll]);
  useEffect(() => {
    if (!token) return;
    api.get("/notifications/my").then((r) => { setNotifs(r.data.notifications); setUnread(r.data.unread); }).catch(() => {});
  }, [token]);

  if (!token) return <Navigate to="/login" replace />;
  if (!user) return <div style={{ padding: 40, color: "#fff", background: "#050505", minHeight: "100vh" }}>{t("common.loading")}</div>;

  const setRolePersist = (r) => {
    setRole(r); localStorage.setItem("dash_role", r); setMobileOpen(false);
    const path = r === "biz" ? "/dashboard/biz" : r === "admin" ? "/dashboard/admin" : "/dashboard";
    navigate(path);
  };
  const isAdmin = user.role === "admin";
  // Force regular role if non-admin tried to enter admin via localStorage
  const effectiveRole = (role === "admin" && !isAdmin) ? "user" : role;
  const NAV = effectiveRole === "biz" ? BIZ_NAV : effectiveRole === "admin" ? ADMIN_NAV : USER_NAV;
  const wallets = storeWallets || user.wallets || { cashTgc: 0, shoppingTgc: 0, pensionTgc: 0, insuranceTgc: 0 };
  const totalTGC = (wallets.cashTgc || 0) + (wallets.shoppingTgc || 0) + (wallets.pensionTgc || 0) + (wallets.insuranceTgc || 0);
  const totalEUR = totalTGC * 0.10;

  return (
    <div className="dash-root">
      {/* SIDEBAR */}
      <aside className={`dash-sidebar ${mobileOpen ? "mobile-open" : ""}`}>
        <Link to="/" className="dash-sidebar-logo">
          <img src="/tgc-logo.png" alt="TGC" style={{ width: 140, height: 140, objectFit: "contain", filter: "drop-shadow(0 0 16px rgba(255,200,50,0.55))" }} />
          <span style={{ fontSize: 22, fontWeight: 900 }}>TURBO <span style={{ color: "#00F2FF" }}>GLOBAL</span></span>
        </Link>
        <div className="dash-role-switch">
          <button className={`dash-role-btn ${effectiveRole === "user" ? "active" : ""}`} onClick={() => setRolePersist("user")} data-testid="role-user">{t("dash.role.user")}</button>
          <button className={`dash-role-btn ${effectiveRole === "biz" ? "active" : ""}`} onClick={() => setRolePersist("biz")} data-testid="role-biz">{t("dash.role.biz")}</button>
          {isAdmin && (
            <button className={`dash-role-btn ${effectiveRole === "admin" ? "active" : ""}`} onClick={() => setRolePersist("admin")} data-testid="role-admin" style={{ background: effectiveRole === "admin" ? "#FFD700" : undefined, color: effectiveRole === "admin" ? "#000" : undefined, borderColor: effectiveRole === "admin" ? "#FFD700" : undefined }}>{t("dash.role.admin")}</button>
          )}
        </div>
        <nav className="dash-nav">
          {NAV.map((g, i) => (
            <div key={i} className="dash-nav-group">
              <div className="dash-nav-title">{g.group}</div>
              {g.items.map(([to, label, Icon]) => (
                <NavLink key={to} to={`/dashboard${to ? "/" + to : ""}`} end={!to}
                  className={({ isActive }) => `dash-nav-item ${isActive ? "active" : ""}`}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`dash-nav-${to || "home"}`}>
                  <Icon size={16} /> {label}
                </NavLink>
              ))}
            </div>
          ))}
          <div className="dash-nav-group">
            <button className="dash-nav-item" onClick={() => setAiOpen(true)} data-testid="open-ai-drawer"><Bot size={16} /> {t("dash.ai_assistant")}</button>
            <button className="dash-nav-item" onClick={() => { logout(); window.location.href = "/"; }} data-testid="dash-logout" style={{ color: "#FF3D6C" }}><LogOut size={16} /> {t("dash.logout")}</button>
          </div>
        </nav>
      </aside>

      {/* MAIN */}
      <div className="dash-main">
        <header className="dash-header">
          <button className="dash-burger" onClick={() => setMobileOpen(!mobileOpen)} data-testid="dash-burger">
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <h1>{effectiveRole === "admin" ? t("dash.title.admin") : effectiveRole === "biz" ? t("dash.title.biz") : t("dash.title.user")}</h1>
          <div className="spacer" />
          <div className="dash-balance-chip" data-testid="total-balance">
            {totalTGC.toFixed(2)} TGC · €{totalEUR.toFixed(2)}
          </div>
          <button className="dash-icon-btn" onClick={() => setBellOpen(true)} data-testid="bell-btn">
            <Bell size={18} />
            {unread > 0 && <span className="dash-bell-dot" />}
          </button>
          <div style={{ width: 38, height: 38, borderRadius: 19, background: "linear-gradient(135deg,#00F2FF,#7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: "#000" }}>
            {user.firstName?.[0] || "U"}{user.lastName?.[0] || ""}
          </div>
        </header>

        <main className="dash-content">
          <Routes>
            {/* User routes */}
            <Route index element={<OverviewPage />} />
            <Route path="wallets"      element={<WalletsPage />} />
            <Route path="staking"      element={<StakingPage />} />
            <Route path="card"         element={<CardPage />} />
            <Route path="transactions" element={<TransactionsPage />} />
            <Route path="charity"      element={<CharityPage />} />
            <Route path="exchange"     element={<ExchangePage />} />
            <Route path="p2p"          element={<P2PPage />} />
            <Route path="swift"        element={<SwiftPage />} />
            <Route path="referrals"    element={<ReferralPage />} />
            <Route path="marketplace"  element={<MarketplacePage />} />
            <Route path="academy"      element={<AcademyPage />} />
            <Route path="security"     element={<SecurityPage />} />
            <Route path="profile"      element={<ProfilePage />} />
            {/* Business routes */}
            <Route path="biz"            element={<BizOverviewPage />} />
            <Route path="biz/acquiring"  element={<AcquiringPage />} />
            <Route path="biz/swift"      element={<SwiftBizPage />} />
            <Route path="biz/analytics"  element={<AnalyticsPage />} />
            <Route path="biz/api"        element={<ApiPage />} />
            <Route path="biz/catalog"    element={<CatalogPage />} />
            <Route path="biz/team"       element={<TeamPage />} />
            <Route path="biz/withdraw"   element={<WithdrawPage />} />
            <Route path="biz/docs"       element={<DocsPage />} />
            {/* Admin routes — protected: only role=="admin" */}
            <Route path="admin"             element={isAdmin ? <AdminOverviewPage />     : <Navigate to="/dashboard" replace />} />
            <Route path="admin/users"       element={isAdmin ? <AdminUsersPage />        : <Navigate to="/dashboard" replace />} />
            <Route path="admin/kyc"         element={isAdmin ? <AdminKycPage />          : <Navigate to="/dashboard" replace />} />
            <Route path="admin/transactions"element={isAdmin ? <AdminTransactionsPage /> : <Navigate to="/dashboard" replace />} />
            <Route path="admin/orders"      element={isAdmin ? <AdminOrdersPage />       : <Navigate to="/dashboard" replace />} />
            <Route path="admin/products"    element={isAdmin ? <AdminProductsPage />     : <Navigate to="/dashboard" replace />} />
            <Route path="admin/leaderboard" element={isAdmin ? <AdminLeaderboardPage /> : <Navigate to="/dashboard" replace />} />
            <Route path="admin/investor-leads" element={isAdmin ? <AdminInvestorLeadsPage /> : <Navigate to="/dashboard" replace />} />
          </Routes>
        </main>
      </div>

      {/* BELL DRAWER */}
      {bellOpen && (
        <div className="dash-drawer" data-testid="bell-drawer">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h3 style={{ margin: 0 }}>🔔 {t("dash.notifications")}</h3>
            <button onClick={() => setBellOpen(false)} className="dash-icon-btn"><X size={18} /></button>
          </div>
          {notifs.map((n) => (
            <div key={n.id} style={{ padding: 14, borderRadius: 12, border: "1px solid var(--tg-border)", marginBottom: 10, background: n.read ? "transparent" : "rgba(0,242,255,0.04)" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{n.title}</div>
              <div style={{ fontSize: 13, color: "var(--tg-text-dim)", marginTop: 4, lineHeight: 1.5 }}>{n.body}</div>
            </div>
          ))}
        </div>
      )}

      {/* AI DRAWER */}
      {aiOpen && (
        <div className="dash-drawer" data-testid="ai-drawer">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h3 style={{ margin: 0 }}><Bot size={18} style={{ verticalAlign: "middle" }} /> Turbo AI Assistant</h3>
            <button onClick={() => setAiOpen(false)} className="dash-icon-btn"><X size={18} /></button>
          </div>
          <AIChat />
        </div>
      )}
    </div>
  );
}

/* Reusable AI chat inside drawer */
function AIChat() {
  const { t } = useT();
  const [msgs, setMsgs] = useState([{ role: "ai", text: t("ai.greeting") }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const send = async () => {
    if (!input.trim()) return;
    const userMsg = input;
    setMsgs((m) => [...m, { role: "user", text: userMsg }]);
    setInput(""); setLoading(true);
    try {
      const r = await api.post("/ai/chat", { message: userMsg });
      setMsgs((m) => [...m, { role: "ai", text: r.data.reply || r.data.response || "..." }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "ai", text: t("ai.error") }]);
    } finally { setLoading(false); }
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 100px)" }}>
      <div style={{ flex: 1, overflowY: "auto", paddingRight: 8 }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 14,
            background: m.role === "user" ? "rgba(0,242,255,0.1)" : "rgba(255,255,255,0.03)",
            alignSelf: m.role === "user" ? "flex-end" : "flex-start",
            maxWidth: "85%", fontSize: 13, lineHeight: 1.5, marginLeft: m.role === "user" ? "auto" : 0 }}>
            {m.text}
          </div>
        ))}
        {loading && <div style={{ color: "var(--tg-text-hint)", fontSize: 12 }}>{t("ai.typing")}</div>}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <input className="auth-input" placeholder={t("ai.placeholder")} value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          style={{ padding: "10px 12px" }} />
        <button className="tg-btn primary" onClick={send} disabled={loading} style={{ padding: "8px 14px" }}>→</button>
      </div>
    </div>
  );
}
